import { QuestionOutlined } from "@ant-design/icons"
import { App, Button, Flex } from "antd"
import { useEffect } from "react"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../constants/routes.ts"
import { LEADS_SAVE_RESULT } from "../../containers/Leads/constants/leads-save-results.ts"
import { leadsSetProp } from "../../containers/Leads/redux/leads.slice.ts"
import { changeLeadStatus } from "../../containers/Leads/redux/leads.thunks.ts"
import { type LeadListItem } from "../../containers/Leads/types/types.ts"
import { useAppDispatch } from "../use-app-dispatch.ts"
import { useAppSelector } from "../use-app-selector.ts"
import { useNotifications } from "../use-notifications.ts"

interface LeadMissingFieldsRejection {
  targetStatus?: number | null
  /** The backend's own field names — what the deep link carries. */
  fields?: string[]
  missing?: string[]
  leadId?: string
}

interface UseLeadStatusChangeArgs {
  refetchCallback: () => void
  /** A surface already showing this lead answers the rejection in place. */
  onMissingFields?: (rejection: LeadMissingFieldsRejection) => void
}

export const useLeadStatusChange = ({
  refetchCallback,
  onMissingFields
}: UseLeadStatusChangeArgs): {
  confirmStatusChange: (
    lead: Pick<LeadListItem, "id">,
    targetStatus: number
  ) => void
} => {
  const { modal } = App.useApp()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const notification = useNotifications()

  const saveResult = useAppSelector((state) => state.leads.saveResult)

  const confirmStatusChange = (
    lead: Pick<LeadListItem, "id">,
    targetStatus: number
  ) => {
    modal.confirm({
      centered: true,
      icon: <QuestionOutlined />,
      title: "Confirm Status Change",
      content: "Are you sure you want to change the status of this lead?",
      cancelText: "Cancel",
      okText: "Change",
      onOk() {
        dispatch(
          changeLeadStatus({
            id: lead?.id,
            target_status: targetStatus
          })
        )
      }
    })
  }

  useEffect(() => {
    if (saveResult) {
      const type = saveResult?.type

      if (type === LEADS_SAVE_RESULT.MISSING_FIELDS) {
        const leadId = saveResult.leadId

        onMissingFields?.({
          targetStatus: saveResult.targetStatus,
          fields: saveResult.fields,
          missing: saveResult.missing,
          leadId
        })

        const description = (
          <Flex vertical>
            <span>Some required fields are missing.</span>
            {saveResult.missing?.length ? (
              <ul className="mb-0 list-disc pl-5">
                {saveResult.missing.map((field) => (
                  <li key={field}>{field}</li>
                ))}
              </ul>
            ) : null}
            <span>Would you like to add the missing information now?</span>
          </Flex>
        )

        notification.warning({
          message: "Required Information Missing",
          description,
          showProgress: true,
          pauseOnHover: true,
          // Without a lead to open, the action would navigate nowhere.
          actions: leadId ? (
            <Button
              onClick={() => {
                // The field names travel with the link so the workspace can
                // mark the cards the message named, rather than matching on the
                // labels, which reword.
                const missingFields = saveResult?.fields?.length
                  ? `&missing=${encodeURIComponent(saveResult.fields.join(","))}`
                  : ""
                navigate(
                  `${RoutesPaths.lead.view(leadId)}?next_status=${saveResult?.targetStatus}${missingFields}`
                )
                notification.destroy()
              }}
              type="primary"
              size="small"
            >
              Update Now
            </Button>
          ) : undefined
        })
      }

      if (type === LEADS_SAVE_RESULT.LEAD_STATUS_UPDATE) {
        refetchCallback()
      }

      dispatch(leadsSetProp({ key: "saveResult", value: null }))
    }
  }, [saveResult])

  return { confirmStatusChange }
}
